import React, { useState, useEffect } from 'react';
import { Box, Tooltip, Typography, Chip, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SavedView } from '../../types/ViewTypes';
import { AutoAwesome, Warning, Save } from '@mui/icons-material';
import { CustomButton } from '../Infra/CustomButton/CustomButton';

interface ActiveViewIndicatorProps {
  view: SavedView | null;
  modifications?: string[];
  onClose?: () => void;
  onUpdateView?: () => void;
}

const ActiveViewIndicator: React.FC<ActiveViewIndicatorProps> = ({
  view,
  modifications = [],
  onClose,
  onUpdateView,
}) => {
  const [showModifications, setShowModifications] = useState(false);
  const hasModifications = modifications.length > 0;

  // Add a delay before showing modifications to prevent flashing
  useEffect(() => {
    if (hasModifications) {
      const timer = setTimeout(() => {
        setShowModifications(true);
      }, 150); // 150ms delay before showing orange state

      return () => clearTimeout(timer);
    } else {
      setShowModifications(false);
    }
  }, [hasModifications]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (!view) return null;

  const tooltipContent = showModifications ? (
    <Box sx={{ maxWidth: 350, p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Warning sx={{ color: 'warning.main', mr: 1, fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {`View "${view.name}" has been modified`}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {modifications.map((mod, index) => {
          // Extract just the change description, removing "before → after" format
          let simpleChange = mod;
          if (mod.includes(' → ')) {
            const parts = mod.split(' → ');
            const prefix = parts[0].split(':')[0];
            const newValue = parts[1];
            simpleChange = `${prefix}: ${newValue}`;
          }

          return (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  mt: 1,
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  lineHeight: 1.4,
                  color: 'text.primary',
                }}
              >
                {simpleChange}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <CustomButton
        data-testid="btn-update-view"
        variant="contained"
        size="small"
        startIcon={<Save fontSize="small" />}
        onClick={onUpdateView}
        sx={{
          width: '100%',
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          '&:hover': {
            bgcolor: 'warning.dark',
          },
          textTransform: 'none',
          fontWeight: 500,
        }}
      >
        Update View with Current State
      </CustomButton>
    </Box>
  ) : (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2">
        Active view: <strong>{view.name}</strong>
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="left"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: showModifications ? 'warning.main' : 'divider',
            boxShadow: 3,
            fontSize: '0.75rem',
            maxWidth: 'none',
          },
        },
        arrow: {
          sx: {
            color: showModifications ? 'warning.main' : 'divider',
          },
        },
      }}
    >
      <Chip
        data-testid="active-view-indicator"
        icon={<AutoAwesome fontSize="small" />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              data-testid="active-view-name"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {view.name}
            </Typography>
            {showModifications && (
              <Typography
                data-testid="view-modified-indicator"
                variant="caption"
                sx={{
                  ml: 1,
                  opacity: 0.8,
                  fontWeight: 400,
                  fontSize: '0.7rem',
                }}
              >
                • modified
              </Typography>
            )}
          </Box>
        }
        deleteIcon={
          onClose ? (
            <CloseIcon data-testid="btn-close-view" fontSize="small" />
          ) : undefined
        }
        onDelete={onClose ? handleClose : undefined}
        color={showModifications ? 'warning' : 'primary'}
        variant="filled"
        sx={{
          height: 32,
          borderRadius: 2,
          '& .MuiChip-label': {
            px: 1,
          },
          '& .MuiChip-icon': {
            ml: 1,
          },
          '& .MuiChip-deleteIcon': {
            mr: 0.5,
            '&:hover': {
              color: 'inherit',
            },
          },
          boxShadow: 1,
        }}
      />
    </Tooltip>
  );
};

export default ActiveViewIndicator;
